import {
  saveDraft,
  updateDraft,
  searchCustomSurveys,
  getCustomSurvey,
  removeCustomSurvey,
  closeCustomSurvey,
  duplicateCustomSurvey,
} from '@/service/custom-surveys'
import { searchGroups } from '@/service/groups'
import { searchUsers } from '@/service/users'

const newId = () => String(new Date().getTime())

const languageOptions = options =>
  options.reduce((map, language) => {
    map[language] = null
    return map
  }, {})

const answer = availableLanguages => ({
  answer_id: newId(),
  text: languageOptions(availableLanguages),
  classification: null,
})

const question = availableLanguages => ({
  question_id: newId(),
  type: 'feedback',
  category: null,
  addCategory: false,
  enableScore: false,
  text: languageOptions(availableLanguages),
  answers: [],
})

const category = availableLanguages => ({
  category_id: newId(),
  label: languageOptions(availableLanguages),
  description: languageOptions(availableLanguages),
  image: null,
  questions: [],
})

const initialState = () => {
  return {
    // create
    id: undefined,
    isTemplate: false,
    status: undefined,
    statusUpdate: {},
    period: '',
    cycle: undefined,
    mandatoryQuestions: undefined,
    enableFeedbacks: undefined,
    questionsDistribution: '',
    questionsQuantity: 0,
    availableLanguages: [],
    questions: [],
    title: {},
    description: {},
    categories: [],
    surveyPublic: {
      sendToCompany: false,
      groupsWithHierarchy: [],
      groupsWithHierarchyOptions: [],
      groupsOnly: [],
      groupsOnlyOptions: [],
      users: [],
      usersOptions: [],
    },
    surveyAccess: {
      managersPermission: false,
      users: [],
      usersOptions: [],
    },
    nextDeliveySurveyDate: undefined,
    launchConfiguration: {},
    // list
    customSurveys: [],
    customSurveysCount: undefined,
  }
}

let state = initialState()

const getters = {
  // create
  id: state => state.id,
  isTemplate: state => state.isTemplate,
  period: state => state.period,
  cycle: state => state.cycle,
  status: state => state.status,
  statusUpdate: state => state.statusUpdate,
  mandatoryQuestions: state => state.mandatoryQuestions,
  enableFeedbacks: state => state.enableFeedbacks,
  questionsDistribution: state => state.questionsDistribution,
  questionsQuantity: state => state.questionsQuantity,
  availableLanguages: state => state.availableLanguages,
  questions: state => state.questions,
  title: state => state.title,
  description: state => state.description,
  categories: state => state.categories,
  surveyPublic: state => state.surveyPublic,
  surveyAccess: state => state.surveyAccess,
  nextDeliveySurveyDate: state => state.nextDeliveySurveyDate,
  launchConfiguration: state => state.launchConfiguration,
  // list
  customSurveys: state => state.customSurveys,
  customSurveysCount: state => state.customSurveysCount,
}

const mutations = {
  setSurveyPublicProperty(state, { property, value }) {
    state['surveyPublic'][property] = value
  },
  setSurveyAccessProperty(state, { property, value }) {
    state['surveyAccess'][property] = value
  },
  setProperty(state, { property, value }) {
    if (property === 'availableLanguages') {
      const languagesToDelete = state[property].filter(l => !value.includes(l))
      languagesToDelete.forEach(l => {
        delete state['title'][l]
        delete state['description'][l]
        state['questions'].forEach(q => {
          delete q.text[l]
          if (['select', 'multi-select'].includes(q.type)) {
            q.answers.forEach(a => delete a.text[l])
          }
        })
        state['categories'].forEach(c => {
          delete c.label[l]
          delete c.description[l]
        })
      })
      const languagesToAdd = value.filter(l => !state[property].includes(l))
      languagesToAdd.forEach(l => {
        state['title'][l] = undefined
        state['description'][l] = undefined
        state['questions'].forEach(q => {
          q.text[l] = undefined
          if (['select', 'multi-select'].includes(q.type)) {
            q.answers.forEach(a => (a.text[l] = undefined))
          }
        })
        state['categories'].forEach(c => {
          c.label[l] = undefined
          c.description[l] = undefined
        })
      })
    }
    state[property] = value
  },
  setLanguageProperty(state, { property, language, value }) {
    state[property][language] = value
  },
  addQuestion(state) {
    state['questions'].push(question(state['availableLanguages']))
  },
  removeQuestion(state, { idx }) {
    const question = state['questions'].find((_, index) => index === idx)
    const category = state['categories'].find(
      c => c.category_id === question.category
    )
    if (category) {
      category.questions = category.questions.filter(
        q => q.question_id !== question.question_id
      )
    }
    state['questions'] = state['questions'].filter((_, index) => index !== idx)
  },
  setLanguageListProperty(
    state,
    { value, collection, index, property, language }
  ) {
    state[collection][index][property][language] = value
  },
  setListProperty(state, { value, collection, index, property }) {
    if (collection === 'questions') {
      if (property === 'category') {
        const question = state['questions'][index]
        const id = question.category

        // remove from current category
        if (id) {
          const category = state['categories'].find(c => c.category_id === id)
          category.questions = state['categories']
            .find(c => c.category_id === id)
            .questions.filter(q => q.question_id !== question.question_id)
        }
        // add to new category
        if (value) {
          state['categories']
            .find(c => c.category_id === value)
            .questions.push({ question_id: question.question_id })
        }
      } else if (property === 'type') {
        state[collection][index]['enableScore'] =
          value === 'multi-select'
            ? false
            : state[collection][index]['enableScore']
      }
    }
    state[collection][index][property] = value
  },
  addCategory(state) {
    state['categories'].push(category(state['availableLanguages']))
  },
  removeCategory(state, { idx }) {
    const categoryId = state['categories'].find((_, index) => index === idx)
      .category_id
    state['questions'].forEach(question => {
      if (question.category === categoryId) {
        question.category = undefined
      }
    })
    state['categories'] = state['categories'].filter(
      (_, index) => index !== idx
    )
  },
  moveQuestion(state, { from, to }) {
    state['questions'].splice(to, 0, state['questions'].splice(from, 1)[0])
  },
  moveCategory(state, { from, to }) {
    state['categories'].splice(to, 0, state['categories'].splice(from, 1)[0])
  },
  createCategoryOfQuestion(state, { questionIndex, categoryLabel, language }) {
    const question = state['questions'][questionIndex]
    const id = question['category']

    // remove from current category
    if (id) {
      const category = state['categories'].find(c => c.category_id === id)
      category.questions = state['categories']
        .find(c => c.category_id === id)
        .questions.filter(q => q.question_id !== question.question_id)
    }

    // add to new category
    const newCategory = category(state['availableLanguages'])
    newCategory.label[language] = categoryLabel

    state['categories'].push(newCategory)
    question['category'] = newCategory.category_id
    question['addCategory'] = false
  },
  setId(state, id) {
    state['id'] = id
  },
  setCustomSurveys(state, { customSurveys, metadata }) {
    const list = customSurveys.map(s => ({
      ...s,
      loadingRemove: false,
      loadingClose: false,
      loadingDuplicate: false,
    }))
    state['customSurveys'] = list
    state['customSurveysCount'] = metadata.total
  },
  resetState(state, language = false) {
    Object.assign(state, initialState())
    if (language) {
      state['availableLanguages'] = [language]
    }
  },
  setCustomSurvey(state, { customSurvey }) {
    state['id'] = customSurvey._id
    delete customSurvey._id

    customSurvey.questions.forEach(q => (q.addCategory = false))

    Object.assign(state['surveyPublic'], customSurvey.surveyPublic)
    customSurvey.surveyPublic = state['surveyPublic']

    Object.assign(state['surveyAccess'], customSurvey.surveyAccess)
    customSurvey.surveyAccess = state['surveyAccess']

    Object.assign(state, customSurvey)
  },
  addAnswer(state, { questionIndex }) {
    if (!state['questions'][questionIndex].answers) {
      state['questions'][questionIndex].answers = []
    }
    state['questions'][questionIndex].answers.push(
      answer(state['availableLanguages'])
    )
  },
  removeAnswer(state, { answerId, questionIndex }) {
    state['questions'][questionIndex].answers = state['questions'][
      questionIndex
    ].answers.filter(a => a.answer_id !== answerId)
  },
  togglePopoverAddCategory(state, { index, value }) {
    state['questions'][index].addCategory = value
  },
  setLanguageListOfListProperty(
    state,
    { value, collection, index, listCollection, listIndex, property, language }
  ) {
    state[collection][index][listCollection][listIndex][property][
      language
    ] = value
  },
  setListOfListProperty(
    state,
    { value, collection, index, listCollection, listIndex, property }
  ) {
    state[collection][index][listCollection][listIndex][property] = value
  },
  removeQuestionFromCategory(state, { categoryId, questionId }) {
    const category = state['categories'].find(c => c.category_id === categoryId)
    category.questions = category.questions.filter(
      q => q.question_id !== questionId
    )

    const question = state['questions'].find(q => q.question_id === questionId)
    question.category = undefined
  },
  removeElementFromListOfObject(state, { object, property, value }) {
    state[object][property] = state[object][property].filter(
      i => (i._id ? i._id : i.id) !== value
    )
  },
  moveUpQuestionAnswer(state, { qIdx, index }) {
    const from = index
    const to = index - 1
    state['questions'][qIdx].answers.splice(
      to,
      0,
      state['questions'][qIdx].answers.splice(from, 1)[0]
    )
  },
  moveDownQuestionAnswer(state, { qIdx, index }) {
    const from = index
    const to = index + 1
    state['questions'][qIdx].answers.splice(
      to,
      0,
      state['questions'][qIdx].answers.splice(from, 1)[0]
    )
  },
  setLoadingStatus(state, { customSurveyId, loadingProperty, status }) {
    const customSurvey = state['customSurveys'].find(
      s => s._id === customSurveyId
    )
    customSurvey[loadingProperty] = status
  },
}

const actions = {
  removeQuestionFromCategory({ commit }, { categoryId, questionId }) {
    commit('removeQuestionFromCategory', { categoryId, questionId })
  },
  async searchForGroups({ commit }, { value, property }) {
    return searchGroups({ name: value, limit: 5 }).then(groups => {
      commit('setSurveyPublicProperty', {
        property,
        value: groups,
      })
    })
  },
  async searchForUsers({ commit }, { value, role = undefined }) {
    const query = { name: value, role, limit: 5, status: 'activated' }
    return searchUsers(query).then(({ users }) => {
      commit('setSurveyPublicProperty', {
        property: 'usersOptions',
        value: users,
      })
      commit('setSurveyAccessProperty', {
        property: 'usersOptions',
        value: users,
      })
    })
  },
  changeSurveyPublicProperty({ commit }, { property, value }) {
    commit('setSurveyPublicProperty', { property, value })
  },
  changeSurveyAccessProperty({ commit }, { property, value }) {
    commit('setSurveyAccessProperty', { property, value })
  },
  changeProperty({ commit }, { property, value }) {
    commit('setProperty', { property, value })
  },
  changeLanguageProperty({ commit }, { property, language, value }) {
    commit('setLanguageProperty', { property, language, value })
  },
  addQuestion({ commit }) {
    commit('addQuestion')
  },
  removeQuestion({ commit }, { idx }) {
    commit('removeQuestion', { idx })
  },
  changeLanguageListProperty(
    { commit },
    { value, collection, index, property, language }
  ) {
    commit('setLanguageListProperty', {
      value,
      collection,
      index,
      property,
      language,
    })
  },
  changeListProperty({ commit }, { value, collection, index, property }) {
    commit('setListProperty', {
      value,
      collection,
      index,
      property,
    })
  },
  addCategory({ commit }) {
    commit('addCategory')
  },
  removeCategory({ commit }, { idx }) {
    commit('removeCategory', { idx })
  },
  moveQuestion({ commit }, { from, to }) {
    commit('moveQuestion', { from, to })
  },
  moveCategory({ commit }, { from, to }) {
    commit('moveCategory', { from, to })
  },
  createCategoryOfQuestion(
    { commit },
    { questionIndex, categoryLabel, language }
  ) {
    commit('createCategoryOfQuestion', {
      questionIndex,
      categoryLabel,
      language,
    })
  },
  async saveDraft({ commit }, draft) {
    if (draft.id) {
      return updateDraft(draft)
    }
    return saveDraft(draft).then(({ data }) => {
      commit('setId', data.customSurvey.id)
    })
  },
  async loadCustomSurveysList({ commit }, query) {
    return searchCustomSurveys(query).then(({ data }) => {
      commit('setCustomSurveys', data)
    })
  },
  resetState({ commit }, language) {
    commit('resetState', language)
  },
  async loadCustomSurvey({ commit }, id) {
    return getCustomSurvey(id).then(({ data }) => {
      commit('setCustomSurvey', data)
    })
  },
  addAnswer({ commit }, { questionIndex }) {
    commit('addAnswer', { questionIndex })
  },
  removeAnswer({ commit }, { answerId, questionIndex }) {
    commit('removeAnswer', { answerId, questionIndex })
  },
  togglePopoverAddCategory({ commit }, { index, value }) {
    commit('togglePopoverAddCategory', { index, value })
  },
  changeLanguageListOfListProperty(
    { commit },
    { value, collection, index, listCollection, listIndex, property, language }
  ) {
    commit('setLanguageListOfListProperty', {
      value,
      collection,
      index,
      listCollection,
      listIndex,
      property,
      language,
    })
  },
  changeListOfListProperty(
    { commit },
    { value, collection, index, listCollection, listIndex, property }
  ) {
    commit('setListOfListProperty', {
      value,
      collection,
      index,
      listCollection,
      listIndex,
      property,
    })
  },
  removeElementFromListOfObject({ commit }, { object, property, value }) {
    commit('removeElementFromListOfObject', { object, property, value })
  },
  moveUpQuestionAnswer({ commit }, { qIdx, index }) {
    commit('moveUpQuestionAnswer', { qIdx, index })
  },
  moveDownQuestionAnswer({ commit }, { qIdx, index }) {
    commit('moveDownQuestionAnswer', { qIdx, index })
  },
  async closeCustomSurvey({ commit }, customSurveyId) {
    commit('setLoadingStatus', {
      customSurveyId,
      loadingProperty: 'loadingClose',
      status: true,
    })
    return closeCustomSurvey(customSurveyId).then(() => {
      commit('setLoadingStatus', {
        customSurveyId,
        loadingProperty: 'loadingClose',
        status: false,
      })
    })
  },
  async removeCustomSurvey({ commit }, customSurveyId) {
    commit('setLoadingStatus', {
      customSurveyId,
      loadingProperty: 'loadingRemove',
      status: true,
    })
    return removeCustomSurvey(customSurveyId).then(() => {
      commit('setLoadingStatus', {
        customSurveyId,
        loadingProperty: 'loadingRemove',
        status: false,
      })
    })
  },
  async duplicateCustomSurvey({ commit }, customSurveyId) {
    commit('setLoadingStatus', {
      customSurveyId,
      loadingProperty: 'loadingDuplicate',
      status: true,
    })
    return duplicateCustomSurvey(customSurveyId).then(() => {
      commit('setLoadingStatus', {
        customSurveyId,
        loadingProperty: 'loadingDuplicate',
        status: false,
      })
    })
  },
}

export default {
  namespaced: true,
  actions,
  getters,
  mutations,
  state,
}
